import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { z } from 'zod';

import type { ContractFormFields } from './types';

interface UseValidationSchemaParams {
  requiredCredentialNames?: string[];
  manualSetupRequired?: boolean;
}

export function useValidationSchema({
  requiredCredentialNames = [],
  manualSetupRequired = false,
}: UseValidationSchemaParams = {}) {
  const intl = useIntl();

  const credentialRequiredMessage = intl.formatMessage({
    defaultMessage: 'This field is required',
    description: 'Add contract sheet: required credential field error',
  });

  const followedInstructionsRequiredMessage = intl.formatMessage({
    defaultMessage: 'You must confirm that you followed the instructions',
    description:
      'Add contract sheet: validation error when manual setup acknowledgment checkbox is unchecked',
  });

  const currencyRequiredMessage = intl.formatMessage({
    defaultMessage: 'This field is required',
    description:
      'Add contract sheet: currency required when purchase price is set',
  });

  return useMemo(
    () =>
      z
        .object({
          alias: z.string(),
          expiryDate: z.string().optional(),
          amount: z.string(),
          currency: z.preprocess(
            value => (value == null ? '' : value),
            z.string(),
          ),
          credits: z.string(),
          postingDurationDays: z.string(),
          credentials: z.record(z.string(), z.string()),
          followedInstructions: z.boolean(),
        })
        .superRefine((data: ContractFormFields, ctx) => {
          for (const name of requiredCredentialNames) {
            if (!(data.credentials[name] ?? '')) {
              ctx.addIssue({
                code: 'custom',
                path: ['credentials', name],
                message: credentialRequiredMessage,
              });
            }
          }

          if (manualSetupRequired && !data.followedInstructions) {
            ctx.addIssue({
              code: 'custom',
              path: ['followedInstructions'],
              message: followedInstructionsRequiredMessage,
            });
          }

          if (data.amount && !(data.currency ?? '')) {
            ctx.addIssue({
              code: 'custom',
              path: ['currency'],
              message: currencyRequiredMessage,
            });
          }
        }),
    [
      credentialRequiredMessage,
      currencyRequiredMessage,
      followedInstructionsRequiredMessage,
      manualSetupRequired,
      requiredCredentialNames,
    ],
  );
}
